import {
  Button,
  formatDateTime,
  type LngLat,
  type MapPoint,
  type MapTruck,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
  ZoneMap,
} from "@majitap/ui";
import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowRight, Info, Truck } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { deliveryHref } from "../../deliveries";
import { fleetQueries } from "../../dispatch";
import { messageFor } from "../../error-copy";
import { DELIVERY_STATUS, type DeliveryStatus } from "../../labels";
import { filterSearch } from "../../list-search";
import { allowed } from "../../nav";
import { RouterLink } from "../../router-link";
import { isLow, mapSensorsQuery, percentText, type Sensor, tankName, tankOnMap } from "../../sensors";
import { meQuery } from "../../session";
import {
  ageOf,
  kmh,
  type TruckPosition,
  type TruckState,
  trailQuery,
  truckLine,
  truckPositionsQuery,
  truckState,
} from "../../tracking";
import { inZone, toMapZone, zonesListQuery } from "../../zones";
import { OwnerFilter } from "./filters";
import styles from "./live-map.module.css";

type Filter = "all" | "moving" | "idle" | "stale";
const FILTERS: [Filter, string][] = [
  ["all", "All"],
  ["moving", "Moving"],
  ["idle", "Idle"],
  ["stale", "Not seen"],
];
/** Not seen first (they need a call), then by plate. */
const ORDER: Record<TruckState, number> = { stale: 0, held: 1, moving: 2, idle: 3 };
const TRAIL_MINUTES = 30;
const ALL = "all";

/** The zone, the owner and the tank sensors, kept in the URL so they stay after a reload. */
export const fleetMapFilters = filterSearch({ tanks: ["on"] as const, zone: "text", owner: "text" });

/**
 * The live fleet map (P8-51, `Console / Fleet / Live map`): every truck on shift where its driver's
 * phone last placed it, refreshed every 10 s; trucks not heard from for 5 minutes are faded. Select
 * one for its last half hour of trail and what it's doing.
 */
export function FleetMapPage(): ReactNode {
  const positions = useQuery(truckPositionsQuery);
  const zones = useQuery(zonesListQuery);
  const fillsites = useQuery(fleetQueries.fillsites);
  const search = useSearch({ from: "/console/fleet/map" });
  const navigate = useNavigate({ from: "/fleet/map" });
  const set = (next: Partial<typeof search>) => navigate({ search: { ...search, ...next }, replace: true });
  const me = useQuery(meQuery);
  const permissions = me.data?.permissions ?? [];
  // the sensors are customers' data: only staff who may manage customers see them
  const canSeeTanks = allowed("customers.manage", permissions);
  const showTanks = canSeeTanks && search.tanks === "on";
  const sensors = useQuery({ ...mapSensorsQuery, enabled: showTanks });
  const [selectedTank, setSelectedTank] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<{ id: string; since: string } | null>(null);
  const select = (id: string) =>
    setSelected({ id, since: new Date(Date.now() - TRAIL_MINUTES * 60_000).toISOString() });
  const trail = useQuery({ ...trailQuery(selected?.id ?? "", selected?.since ?? ""), enabled: Boolean(selected) });

  const zone = zones.data?.find((z) => z.id === search.zone) ?? null;
  const inPlace = (at: LngLat) => !zone || inZone(at, zone);
  // look at the chosen zone; a new array each render would refit the map on every refresh
  const focus = useMemo(() => (zone ? toMapZone(zone).rings.flat() : null), [zone]);
  const onShift = useMemo(() => (positions.data?.data ?? []).filter((p) => p.on_shift), [positions.data]);
  /** the owner filter, as on the fleet lists: "own", "partners" or one partner's id */
  const ownedBy = (p: TruckPosition) =>
    !search.owner ||
    (search.owner === "own"
      ? !p.partner
      : search.owner === "partners"
        ? Boolean(p.partner)
        : p.partner?.id === search.owner);
  // the zone and the owner narrow everything on the page; the counts are of what they leave
  const inScope = onShift.filter((p) => inPlace([p.lng, p.lat]) && ownedBy(p));
  const counts = { all: inScope.length, moving: 0, idle: 0, stale: 0, held: 0 };
  for (const p of inScope) counts[truckState(p)] += 1;
  const needle = q.trim().toLowerCase();
  const shown = inScope
    .filter((p) => {
      const state = truckState(p);
      if (filter === "moving" && state !== "moving") return false;
      if (filter === "idle" && state !== "idle" && state !== "held") return false;
      if (filter === "stale" && state !== "stale") return false;
      return !needle || `${p.plate_number} ${p.driver?.name ?? ""}`.toLowerCase().includes(needle);
    })
    .sort((a, b) => ORDER[truckState(a)] - ORDER[truckState(b)] || a.plate_number.localeCompare(b.plate_number));
  const trucks: MapTruck[] = shown.map((p) => ({
    id: p.truck_id,
    lng: p.lng,
    lat: p.lat,
    state: truckState(p),
    label: p.plate_number,
    title: `${p.plate_number}, ${truckLine(p)}`,
  }));
  const sites: MapPoint[] = (fillsites.data ?? []).flatMap((f) =>
    f.latitude != null && f.longitude != null
      ? [{ id: f.id, lng: f.longitude, lat: f.latitude, label: f.name, tone: "site" as const, tag: f.name }]
      : [],
  );
  // the tanks with a point: sensors on a tank whose place has coordinates
  const placed = showTanks
    ? (sensors.data ?? []).flatMap((s) => {
        const tank = tankOnMap(s);
        return tank && inPlace([tank.lng, tank.lat]) ? [{ sensor: s, tank }] : [];
      })
    : [];
  const tanks = placed.map((p) => p.tank);
  const tankCounts = { ok: 0, low: 0, none: 0 };
  for (const t of tanks) tankCounts[t.state] += 1;
  const chosenTank = placed.find((p) => p.sensor.id === selectedTank)?.sensor ?? null;
  const chosen = shown.find((p) => p.truck_id === selected?.id);
  const trailLine: LngLat[] | null = trail.data && chosen ? trail.data.points.map((p) => [p.lng, p.lat]) : null;

  if (positions.status === "pending") return <PageLoading label="Loading the trucks" />;
  if (positions.status === "error")
    return <PageError message={messageFor(errorOf(positions.error))} onRetry={() => positions.refetch()} />;

  return (
    <>
      <PageHeader
        title="Live map"
        description={`${counts.all} ${counts.all === 1 ? "truck" : "trucks"} on shift · ${counts.moving} moving · ${counts.idle} idle · ${counts.held} held · ${counts.stale} not seen for over 5 minutes`}
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate({ to: "/fleet" })}>
              Fleet list
            </Button>
            {permissions.includes("deliveries.dispatch") ? (
              <Button onClick={() => navigate({ to: "/dispatch" })}>Open Dispatch</Button>
            ) : null}
          </>
        }
      />
      <div className={styles.toolbar}>
        <SegmentedControl
          label="Show"
          value={filter}
          onValueChange={(v) => setFilter(v as Filter)}
          options={FILTERS.map(([value, label]) => ({
            value,
            label,
            accessory: (
              <span className={styles.count} data-tone={value === "stale" && counts.stale ? "warning" : undefined}>
                {value === "idle" ? counts.idle + counts.held : counts[value]}
              </span>
            ),
          }))}
        />
        <div className={styles.bare}>
          <Select
            label="Zone"
            value={search.zone ?? ALL}
            onValueChange={(v) => set({ zone: v === ALL ? undefined : v })}
            options={[
              { value: ALL, label: "All zones" },
              ...(zones.data ?? []).map((z) => ({ value: z.id, label: z.name })),
            ]}
          />
        </div>
        <div className={styles.bare}>
          <OwnerFilter
            value={search.owner}
            onChange={(owner) => set({ owner })}
            partners={permissions.includes("partners.manage")}
          />
        </div>
        <div className={`${styles.bare} ${styles.search}`}>
          <SearchField label="Find a truck" placeholder="Plate or driver" value={q} onValueChange={setQ} />
        </div>
        {canSeeTanks ? (
          <Switch
            label={
              showTanks && sensors.data
                ? `Tank sensors · ${tanks.length}`
                : showTanks && sensors.isError
                  ? "Tank sensors · not loaded"
                  : "Tank sensors"
            }
            checked={showTanks}
            onCheckedChange={(on) => {
              if (!on) setSelectedTank(null);
              set({ tanks: on ? "on" : undefined });
            }}
          />
        ) : null}
        <span className={styles.live}>
          <span className={styles.liveDot} aria-hidden /> Live · refreshes every 10 s
        </span>
      </div>
      {onShift.length === 0 ? (
        <PageEmpty
          title="No truck on shift is sending its location"
          description="Trucks show here once their driver starts a shift in the driver app."
        />
      ) : (
        <div className={styles.columns}>
          <ZoneMap
            label="Trucks on shift"
            height="100%"
            zones={(zones.data ?? []).map(toMapZone)}
            focus={focus}
            points={sites}
            pointsName="Fill sites"
            trucks={trucks}
            selectedTruckId={selected?.id ?? null}
            onSelectTruck={select}
            truckCallout={chosen ? <TruckCallout truck={chosen} /> : null}
            trail={trailLine}
            trailTone="accent"
            tanks={tanks}
            selectedTankId={chosenTank?.id ?? null}
            onSelectTank={(id) => setSelectedTank((cur) => (cur === id ? null : id))}
            tankCallout={chosenTank ? <TankCallout sensor={chosenTank} /> : null}
            fitAll
            legend={
              <span className={styles.legend}>
                <span className={styles.key} data-state="moving">{`Moving ${counts.moving}`}</span>
                <span className={styles.key} data-state="idle">{`Idle ${counts.idle}`}</span>
                <span className={styles.key} data-state="held">{`Held ${counts.held}`}</span>
                <span className={styles.key} data-state="stale">{`Not seen 5+ min ${counts.stale}`}</span>
                <span className={styles.key} data-state="site">
                  Fill site
                </span>
                {showTanks ? (
                  <>
                    <span className={styles.legendDivider} aria-hidden />
                    <span className={styles.key} data-tank="ok">{`Tank OK ${tankCounts.ok}`}</span>
                    <span className={styles.key} data-tank="low">{`Tank low ${tankCounts.low}`}</span>
                    <span className={styles.key} data-tank="none">{`No reading ${tankCounts.none}`}</span>
                  </>
                ) : null}
              </span>
            }
          />
          <section className={styles.side} aria-labelledby="trucks-on-shift">
            <header className={styles.sideHead}>
              <h2 id="trucks-on-shift">Trucks on shift</h2>
              <p>Not seen longest first, then by plate</p>
            </header>
            {shown.length ? (
              <ul className={styles.list}>
                {shown.map((p) => (
                  <TruckRow
                    key={p.truck_id}
                    truck={p}
                    selected={p.truck_id === selected?.id}
                    onSelect={() => select(p.truck_id)}
                  />
                ))}
              </ul>
            ) : (
              <p className={styles.none}>No truck on shift matches these filters.</p>
            )}
            <p className={styles.foot}>
              <Info size={14} aria-hidden />
              From the drivers' phones while on shift. Not seen for 5 minutes: greyed.
            </p>
          </section>
        </div>
      )}
    </>
  );
}

/** "34 km/h", "Idle", "Held at the customer", "Not seen for 14 min": the state, without when it was seen. */
function stateText(p: TruckPosition) {
  const state = truckState(p);
  if (state === "stale") return `Not seen for ${ageOf(p.recorded_at)}`;
  if (state === "held") return "Held at the customer";
  return state === "moving" ? `${kmh(p.speed_mps)} km/h` : "Idle";
}

/** "on the way", "at the customer": a delivery's status, as a phrase after its number. */
const jobText = (status: string) => (DELIVERY_STATUS[status as DeliveryStatus]?.[0] ?? status).toLowerCase();

/** The selected truck, beside it on the map: how it's moving, who drives it and its job. */
function TruckCallout({ truck: p }: { truck: TruckPosition }) {
  const state = truckState(p);
  return (
    <div className={styles.callout}>
      <span className={styles.calloutTop}>
        <strong>{p.plate_number}</strong>
        <span>{state === "stale" ? stateText(p) : `${stateText(p)} · ${ageOf(p.recorded_at)} ago`}</span>
      </span>
      <span className={styles.calloutLevel}>
        {[
          p.driver?.name ?? "No driver",
          p.delivery ? `${p.delivery.delivery_number}, ${jobText(p.delivery.status)}` : "no job",
        ].join(" · ")}
      </span>
      <span className={styles.calloutSub}>
        {[p.partner?.name, `trail: last ${TRAIL_MINUTES} min`].filter(Boolean).join(" · ")}
      </span>
    </div>
  );
}

/** The selected tank, beside it on the map: whose it is, its level, and a way to their page. */
function TankCallout({ sensor: s }: { sensor: Sensor }) {
  const format = useCompanyFormat();
  const owner = s.tank?.owner;
  const low = isLow(s);
  return (
    <div className={styles.callout}>
      <span className={styles.calloutTop}>
        <strong>{tankName(s)}</strong>
        {s.tank?.label ? <span>{s.tank.label}</span> : null}
      </span>
      <span className={low ? styles.calloutLow : styles.calloutLevel}>
        {s.latest
          ? [
              `${percentText(s.latest.level_percent)}%`,
              s.latest.litres === null
                ? null
                : s.tank?.capacity_litres
                  ? `${s.latest.litres.toLocaleString("en-KE")} of ${s.tank.capacity_litres.toLocaleString("en-KE")} L`
                  : `${s.latest.litres.toLocaleString("en-KE")} L`,
            ]
              .filter(Boolean)
              .join(" · ")
          : "No reading yet"}
        {s.status !== "active" ? ` · sensor ${s.status === "faulty" ? "faulty" : "off"}` : ""}
      </span>
      <span className={styles.calloutSub}>
        {[
          low && s.low_alert_at ? `Low since ${formatDateTime(s.low_alert_at, format)}` : null,
          s.tank ? `alert at ${percentText(s.tank.low_level_percent)}%` : null,
          s.latest ? `read ${ageOf(s.latest.measured_at)} ago` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </span>
      {owner ? (
        <RouterLink
          href={owner.type === "business" ? `/businesses/${owner.id}` : `/customers/${owner.id}`}
          className={styles.calloutLink}
        >
          Open {owner.type} <ArrowRight size={12} aria-hidden />
        </RouterLink>
      ) : null}
    </div>
  );
}

/**
 * A truck in the list: its mark, plate, driver and what it's doing, and how long since it was seen.
 * The whole row selects it (a button stretched under the content); the delivery number opens the
 * delivery.
 */
function TruckRow({ truck: p, selected, onSelect }: { truck: TruckPosition; selected: boolean; onSelect: () => void }) {
  const state = truckState(p);
  return (
    <li className={styles.row} data-state={state} data-selected={selected}>
      <button
        type="button"
        className={styles.rowButton}
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={`${p.plate_number}, ${truckLine(p)}`}
      />
      <span className={styles.mark} aria-hidden>
        <Truck size={15} />
      </span>
      <span className={styles.body}>
        <span className={styles.rowTop}>
          <strong>{p.plate_number}</strong>
          <span className={styles.muted}>
            {[p.driver?.name ?? "No driver", p.partner?.name].filter(Boolean).join(" · ")}
          </span>
        </span>
        <span className={styles.status}>
          {stateText(p)}
          {" · "}
          {p.delivery ? (
            <>
              <RouterLink href={deliveryHref(p.delivery.order_id, p.delivery.id)} className={styles.job}>
                {p.delivery.delivery_number}
              </RouterLink>{" "}
              {jobText(p.delivery.status)}
            </>
          ) : (
            "no job"
          )}
        </span>
      </span>
      <span className={styles.age}>{ageOf(p.recorded_at)}</span>
    </li>
  );
}
